import { type ReactNode } from "react";
import { FloatingPortal } from "@floating-ui/react";
import {
  CheckIcon,
  ChevronRightIcon,
} from "@heroicons/react/20/solid";
import {
  DocumentIcon,
  ViewfinderCircleIcon,
} from "@heroicons/react/24/outline";
import { usePopover } from "../hooks/usePopover";
import { type AspectRatioGuide, useSettingsState } from "../settingsStore";
import { classNames } from "../utils";

const ASPECT_RATIO_VALUES: ReadonlyArray<AspectRatioGuide> = [
  "2.35",
  "1.85",
  "16:9",
  "9:16",
  "4:5",
  "3:4",
  "4:3",
  "1:1",
];

interface SettingsPopoverProps {
  /** The trigger element — typically the cog icon button. Rendered as-is; we attach popover props. */
  children: ReactNode;
}

/**
 * Popover anchored to its trigger, containing the player's display settings:
 * aspect-ratio guide (with nested submenu) and white-background toggle.
 *
 * Floating-ui handles positioning with auto-flip and shift to keep it inside
 * the viewport, click-outside dismissal, and escape-to-close.
 */
export function SettingsPopover({ children }: SettingsPopoverProps) {
  const { isOpen, refs, floatingStyles, getReferenceProps, getFloatingProps } =
    usePopover({ placement: "top-end" });

  const {
    videoGuide,
    setVideoGuide,
    videoGuideMaskEnabled,
    setVideoGuideMaskEnabled,
    videoBackgroundWhite,
    setVideoBackgroundWhite,
  } = useSettingsState();

  return (
    <>
      <button
        type="button"
        ref={refs.setReference}
        {...getReferenceProps()}
        className="flex h-6 w-6 items-center justify-center rounded-md text-[#282A30] transition-colors hover:bg-[#F7F8F9] hover:text-black"
        aria-label="Settings"
      >
        {children}
      </button>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-50 w-48 rounded-lg border border-[#DFE1E4] bg-white p-1 text-[13px] text-[#282A30] shadow-[0_8px_28px_-4px_rgba(0,0,0,0.18),_0_0_0_0.5px_rgba(0,0,0,0.06)]"
          >
            <GuidesSubmenuTrigger
              currentGuide={videoGuide}
              onSelectGuide={setVideoGuide}
              maskEnabled={videoGuideMaskEnabled}
              onToggleMask={setVideoGuideMaskEnabled}
            />
            <ToggleItem
              icon={<DocumentIcon className="h-4 w-4" />}
              label="White background"
              enabled={videoBackgroundWhite}
              onChange={setVideoBackgroundWhite}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

// ---- Items ----

interface GuidesSubmenuTriggerProps {
  currentGuide: AspectRatioGuide;
  onSelectGuide: (g: AspectRatioGuide) => void;
  maskEnabled: boolean;
  onToggleMask: (e: boolean) => void;
}

function GuidesSubmenuTrigger({
  currentGuide,
  onSelectGuide,
  maskEnabled,
  onToggleMask,
}: GuidesSubmenuTriggerProps) {
  // Small offset (4px) so the popovers don't touch — each keeps its own
  // shadow visible on the meeting edge. Hover-to-open via safePolygon so
  // the cursor can move diagonally toward the submenu without it closing.
  const { isOpen, refs, floatingStyles, getReferenceProps, getFloatingProps } =
    usePopover({ placement: "left-start", offset: 4, hover: true });

  const valueLabel = currentGuide ?? "Off";

  return (
    <>
      <button
        type="button"
        ref={refs.setReference}
        {...getReferenceProps()}
        className={classNames(
          "flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left transition-colors hover:bg-[#F7F8F9]",
          isOpen && "bg-[#F7F8F9]",
        )}
      >
        <span className="flex items-center gap-2">
          <ViewfinderCircleIcon className="h-4 w-4 text-[#474C57]" />
          <span>Guides</span>
        </span>
        <span className="flex items-center gap-0.5 text-[12px] text-[#71717A]">
          {valueLabel}
          <ChevronRightIcon className="h-3.5 w-3.5" />
        </span>
      </button>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-50 w-36 rounded-lg border border-[#DFE1E4] bg-white p-1 text-[13px] text-[#282A30] shadow-[0_8px_28px_-4px_rgba(0,0,0,0.18),_0_0_0_0.5px_rgba(0,0,0,0.06)]"
          >
            {ASPECT_RATIO_VALUES.map((value) => (
              <RadioItem
                key={value}
                label={value!}
                selected={currentGuide === value}
                onSelect={() => onSelectGuide(value)}
              />
            ))}
            <RadioItem
              label="Off"
              selected={currentGuide === null}
              onSelect={() => onSelectGuide(null)}
            />
            <div className="my-1 border-t border-[#DFE1E4]" />
            <ToggleItem
              label="Show Mask"
              enabled={maskEnabled}
              onChange={onToggleMask}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

interface RadioItemProps {
  label: string;
  selected: boolean;
  onSelect: () => void;
}

function RadioItem({ label, selected, onSelect }: RadioItemProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={classNames(
        "flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left transition-colors hover:bg-[#F7F8F9]",
        selected && "bg-[#F7F8F9]",
      )}
      role="menuitemradio"
      aria-checked={selected}
    >
      <span>{label}</span>
      {selected && <CheckIcon className="h-3.5 w-3.5 text-[#6526DB]" />}
    </button>
  );
}

interface ToggleItemProps {
  icon?: ReactNode;
  label: string;
  enabled: boolean;
  onChange: (next: boolean) => void;
}

function ToggleItem({ icon, label, enabled, onChange }: ToggleItemProps) {
  return (
    <button
      type="button"
      onClick={() => onChange(!enabled)}
      className="flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left transition-colors hover:bg-[#F7F8F9]"
      role="menuitemcheckbox"
      aria-checked={enabled}
    >
      <span className="flex items-center gap-2">
        {icon && <span className="text-[#474C57]">{icon}</span>}
        <span>{label}</span>
      </span>
      {enabled && <CheckIcon className="h-3.5 w-3.5 text-[#6526DB]" />}
    </button>
  );
}
